import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getTicketsBySubmitter, getTemplateGroups, getMode, getCameraMode, getReaders, getPeriods } from '../../api/axios';
import Table from '../Table/Table';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Grid } from '@mui/material';

const MyIssuedTickets = () => {
  const { t } = useTranslation('global');
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [templateGroups, setTemplateGroups] = useState([]);
  const [mode, setMode] = useState("0");
  const [cameraMode, setCameraMode] = useState("0");
  const [readers, setReaders] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [openReadersModal, setOpenReadersModal] = useState(false);
  const [openPeriodsModal, setOpenPeriodsModal] = useState(false);
  const [selectedTicketReaders, setSelectedTicketReaders] = useState([]);
  const [selectedTicketPeriods, setSelectedTicketPeriods] = useState([]);

  const location = useLocation();
  const { startDate, endDate, submitter } = location.state || {};

  const formatDateTime = (change_time, daysOffset, time) => {
    const date = new Date(change_time);
    date.setDate(date.getDate() + daysOffset);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day} ${time}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!submitter || !startDate || !endDate) {
        setLoading(false);
        return;
      }

      try {
        const mode = await getMode();
        const cameraMode = await getCameraMode();
        const groups = await getTemplateGroups();
        const readersData = await getReaders();
        const periodsData = await getPeriods();
        const res = await getTicketsBySubmitter(submitter, startDate, endDate);

        setMode(mode);
        setCameraMode(cameraMode);
        setReaders(readersData);
        setPeriods(Array.isArray(periodsData) ? periodsData : []);

        const formatted = res.map(ticket => ({
          ...ticket,
          h_from: formatDateTime(ticket.change_time, 0, ticket.h_from),
          h_to: formatDateTime(ticket.change_time, ticket.day_no - 1, ticket.h_to),
          active: ticket.active === 1 || ticket.active === '1' ? t('reports.yes') : t('reports.no'),
        }));

        setTickets(formatted);
        setTemplateGroups(groups);
      } catch {
        setTickets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, submitter, t]);

  const getReaderNames = (readersString) => {
    if (!readersString) return '';
    const readerIds = readersString.split(',').map(id => id.trim());
    const readerNames = readerIds.map(id => {
      const reader = readers.find(r => r.id.toString() === id.toString());
      return reader ? reader.name : id;
    });
    return readerNames.join(', ');
  };

  const handleOpenReadersModal = (readersString) => {
    if (!readersString) {
      setSelectedTicketReaders([]);
    } else {
      const readerIds = readersString.split(',').map(id => id.trim());
      setSelectedTicketReaders(readerIds);
    }
    setOpenReadersModal(true);
  };

  const handleCloseReadersModal = () => {
    setOpenReadersModal(false);
    setSelectedTicketReaders([]);
  };

  const handleOpenPeriodsModal = (periodsString) => {
    if (!periodsString) {
      setSelectedTicketPeriods([]);
    } else {
      const periodIds = periodsString.split(',').map(id => id.trim().toString());
      setSelectedTicketPeriods(periodIds);
    }
    setOpenPeriodsModal(true);
  };

  const handleClosePeriodsModal = () => {
    setOpenPeriodsModal(false);
    setSelectedTicketPeriods([]);
  };

  const columns = [
    { heading: t('reports.ticket'), value: 'ticket_id' },
    { heading: t('reports.name'), value: 'name' },
    { heading: t('reports.surname'), value: 'surname' },
    { heading: t('reports.phone'), value: 'phone' },
    { heading: t('reports.dateCreated'), value: 'create_time' },
    { heading: t('reports.from'), value: 'h_from' },
    { heading: t('reports.until'), value: 'h_to' },
    { heading: t('reports.numberOfDays'), value: 'day_no' },
    { heading: t('reports.entryNumber'), value: 'entry_no' },
    { heading: t('reports.dailyNumberOfEntries'), value: 'daily_no' },
    { heading: t('reports.allowedDays'), value: 'allowed_days' },
    { heading: t('reports.submitter'), value: 'submitter' },
    { heading: t('reports.active'), value: 'active' },
    { heading: t('reports.readers'), value: '_readersAction' },
    { heading: t('reports.periods'), value: '_periodsAction' }
  ];

  return (
    <>
      <h2>{t('reports.myIssuedTickets')}</h2>

      {loading ? (
        <p>{t('reports.loading')}</p>
      ) : (
        <Table
          data={tickets}
          column={columns}
          onReadersClick={handleOpenReadersModal}
          onPeriodsClick={handleOpenPeriodsModal}
        />
      )}

      {/* Readers Modal */}
      <Dialog 
        open={openReadersModal} 
        onClose={handleCloseReadersModal} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>{t('reports.readers')}</DialogTitle>
        <DialogContent>
          {selectedTicketReaders.length === 0 ? (
            <Typography>{t('reports.noReaders')}</Typography>
          ) : (
            <Grid container spacing={1}>
              {readers
                .filter(reader => selectedTicketReaders.includes(reader.id.toString()))
                .map((reader) => (
                  <Grid item xs={12} sm={6} key={reader.id}>
                    <Typography>{reader.name}</Typography>
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReadersModal} variant="contained" color="primary">
            {t('reports.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Periods Modal */}
      <Dialog 
        open={openPeriodsModal} 
        onClose={handleClosePeriodsModal} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>{t('reports.periods')}</DialogTitle>
        <DialogContent>
          {selectedTicketPeriods.length === 0 ? (
            <Typography>{t('reports.noPeriods')}</Typography>
          ) : (
            <Grid container spacing={1}>
              {periods
                .filter(period => selectedTicketPeriods.includes(period.id.toString()))
                .map((period) => (
                  <Grid item xs={12} sm={6} key={period.id}>
                    <Typography>{period.start} - {period.end} ({period.number_of_passes} {t('period.passes')})</Typography>
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePeriodsModal} variant="contained" color="primary">
            {t('reports.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MyIssuedTickets;

